import Dexie from "dexie";
import { z } from "zod/v4";

export class Collection<DomainType, RecordType> {
  private schema: z.ZodType<RecordType, DomainType>;
  private collection: Dexie.Collection<RecordType, any>;

  constructor(
    schema: z.ZodType<RecordType, DomainType>,
    collection: Dexie.Collection<RecordType, any>,
  ) {
    this.schema = schema;
    this.collection = collection;
  }

  async toArray(): Promise<DomainType[]> {
    const records = await this.collection.toArray();
    return records.map((record) => this.schema.encode(record));
  }

  async groupBy<K extends keyof DomainType>(key: K): Promise<Record<string, DomainType[]>> {
    const result: Record<string, DomainType[]> = {};
    await this.collection.each((item) => {
      const domainItem = this.schema.encode(item);
      if (!domainItem[key]) {
        return;
      }

      const groupKey = String(domainItem[key]);
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(domainItem);
    });
    return result;
  }

  filter(fn: (item: DomainType) => boolean) {
    this.collection = this.collection.filter((item) => fn(this.schema.encode(item)));
    return this;
  }

  async each(fn: (item: DomainType) => void): Promise<void> {
    await this.collection.each((item) => {
      fn(this.schema.encode(item));
    });
  }

  async map<U>(fn: (item: DomainType) => U): Promise<U[]> {
    const result: U[] = [];
    await this.collection.each((item) => {
      result.push(fn(this.schema.encode(item)));
    });

    return result;
  }
}
